<!-- see https://kazupon.github.io/vue-i18n/guide/component.html#shared-locale-messages-for-components -->
<i18n src="@/translations/FinCreatePortfolioDialog.json"></i18n>

<template>
  <fin-dialog :max-width="550"
              :value="value"
              @input="val => $emit('input', val)">

    <template #title>
      <fin-lexicon-text v-if="modeImport"
                        :text="$t('FinCreatePortfolioDialog.import_portfolio_dialog_title')"
                        auto-solve/>
      <span v-else
            v-html="$t('FinCreatePortfolioDialog.create_portfolio_dialog_title')"/>
    </template>

    <template>
      <v-form v-model="valid" enctype="multipart/form-data">
        <p class="mb-2" v-html="$t('FinCreatePortfolioDialog.portfolio_name_title')"/>
        <v-text-field v-model="form.name"
                      background-color="#fff"
                      class="fin-confirm-dialog-content__text-field"
                      :label="$t('FinCreatePortfolioDialog.portfolio_name_label')"
                      outlined
                      required
                      :rules="rules.portfolioName"/>

        <div v-if="modeImport">
          <p v-if="isInitial" class="mb-2" v-html="$t('FinCreatePortfolioDialog.choose_file_to_upload')"/>
          <v-file-input :disabled="isSaving"
                        required
                        @change="fileChange"
                        v-model="form.importData"
                        class="input-file pt-0 mt-0"
                        append-icon="mdi-paperclip"
                        prepend-icon=""
                        outlined
                        :rules="rules.uploadData"
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        :label="$t('FinCreatePortfolioDialog.choose_file_to_upload_label')"/>
          <p v-if="isSaving" v-html="$t('FinCreatePortfolioDialog.uploading')"/>


          <fin-success-msg v-if="isSuccess && messageDetails.length <= 1"
                           :msg="$t('FinCreatePortfolioDialog.uploaded_success_msg')"/>
          <fin-info-msg v-if="isSuccess && messageDetails.length > 1"
                        :msg="$t('FinCreatePortfolioDialog.uploaded_success_info_msg')"/>

          <ul v-if="isSuccess && messageDetails">
            <li v-for="(e, index) in messageDetails"
                :key="index"
                style="line-height: 21px; font-size: 14px;">
              {{ e }}
            </li>
          </ul>


          <fin-error-msg v-if="isFailed && customErrorMsg"
                         :msg="customErrorMsg"/>
          <ul v-if="isFailed && messageDetails">
            <li v-for="(e, index) in messageDetails"
                :key="index"
                style="line-height: 21px; font-size: 14px;">
              {{ e }}
            </li>
          </ul>
        </div>

        <div v-else>
          <p class="mb-2" v-html="$t('FinCreatePortfolioDialog.capital_field_label')"/>
          <!-- TODO Format EN / DE -->
          <fin-currency-field v-model="form.capital"
                              required
                              :error-messages="form.capital && form.capital > 0 ? '' : $t('FinCreatePortfolioDialog.capital_field_error_negative_error')"
                              :requiredErrorMessage="$t('FinCreatePortfolioDialog.capital_field_required_msg')"/>
        </div>
      </v-form>
    </template>

    <template #actions>
      <v-btn v-if="isSuccess"
             class="mr-2"
             :to="routeToNewPortfolio"
             v-html="$t('FinCreatePortfolioDialog.button_goto_portfolio')"/>
      <v-btn v-if="modeImport"
             :disabled="!valid"
             :loading="loading"
             @click="$emit('confirm', form)"
             dark
             v-html="$t('FinCreatePortfolioDialog.button_import_portfolio')"/>
      <v-btn v-else
             :disabled="!valid"
             :loading="loading"
             @click="$emit('confirm', form)"
             dark
             v-html="$t('FinCreatePortfolioDialog.button_create_portfolio')"/>
    </template>
  </fin-dialog>
</template>

<script>
import FinCurrencyField from "@/components/common/CurrencyField"
import FinErrorMsg from "@/components/common/ErrorMsg"
import FinLexiconText from "@/components/common/lexicon/LexiconText"
import FinSuccessMsg from "@/components/common/SuccessMsg"
import FinInfoMsg from "@/components/common/InfoMsg"

const STATUS_INITIAL = 0, STATUS_SAVING = 1, STATUS_SUCCESS = 2, STATUS_FAILED = 3

// xlsx all platforms, cvs desktop, csv android, csv ios
const allowedDataTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel", "text/comma-separated-values", "text/csv"]


export default {
  name: "FinCreatePortfolioDialog",
  components: {FinInfoMsg, FinSuccessMsg, FinLexiconText, FinErrorMsg, FinCurrencyField},
  props: {
    /** If set to true the dialog is shown */
    value: {
      type: Boolean
    },
    /** If set the action button is disabled */
    loading: {
      type: Boolean,
      default: false
    },
    /** if set to true, the dialog allows the user to upload a portfolio file */
    modeImport: {
      type: Boolean,
      default: false
    },
    /** The status of the current action (create or import) */
    currentStatus: {
      type: Number,
      default: null
    },
    /** The error message to be shown */
    errorMsg: {
      type: String,
    },
    /** A list of (error-) messages to display as ul>li */
    messageDetails: {
      type: Array
    },
    /** The ID of the created portfolio */
    newPortfolioId: {
      type: String
    }
  },
  data() {
    return {
      rules: {
        portfolioName: [v => !!v || this.$t('FinCreatePortfolioDialog.portfolio_name_error')],
        uploadData: [v => (v && v.size > 0) || this.$t('FinCreatePortfolioDialog.choose_file_to_upload_please_select'),
          v => {
            try {
              let result = false
              allowedDataTypes.every(type => {
                result = v.type === type
                return !result
              })
              return result || this.$t('FinCreatePortfolioDialog.choose_file_to_upload_format_error')
            } catch (e) {
              return false
            }
          }]
      },
      form: {
        name: null,
        capital: 0,
        importData: null
      },
      valid: false,
    }
  },
  computed: {
    routeToNewPortfolio() {
      return {name: 'portfolioDetail', params: {id: this.newPortfolioId}}
    },
    customErrorMsg() {
      return this.errorMsg || this.$t('FinCreatePortfolioDialog.uploaded_failure_msg')
    },
    isInitial() {
      return this.currentStatus === STATUS_INITIAL
    },
    isSaving() {
      return this.currentStatus === STATUS_SAVING
    },
    isSuccess() {
      return this.currentStatus === STATUS_SUCCESS
    },
    isFailed() {
      return this.currentStatus === STATUS_FAILED
    },
  },
  methods: {
    resetImport() {
      this.$emit("statusChanged", STATUS_INITIAL)
      this.form.name = undefined
      this.form.capital = 0
      this.form.importData = undefined
    },
    fileChange(file) {
      if (!file) {
        return
      }
      this.form.importData = file
    },
  },
  watch: {
    value() {
      // reset on close
      if (!this.value) {
        this.resetImport()
      }
    }
  },
}
</script>

<style scoped lang="scss">
.radio-button-text {
  position: relative;
}
</style>
